import { supabase } from "@/integrations/supabase/client";
import { User } from "@/types";

interface SubscriptionExpiryData {
  user_id: number;
  status: string;
  subscription_end: string | null;
}

// Helper function to filter users by their subscription expiry status
export const filterUsersByExpiryStatus = async (
  users: User[],
  expiryFilter: string
): Promise<User[]> => {
  try {
    // If no expiry filter is applied, return all users
    if (!expiryFilter) {
      return users;
    }

    // Fetch active subscriptions with expiry dates from user_subscriptions table
    const { data: subscriptionsData, error: subscriptionsError } =
      await supabase
        .from("user_subscriptions")
        .select("user_id, status, subscription_end");

    if (subscriptionsError) {
      console.error("Error fetching subscription expiry:", subscriptionsError);
      throw subscriptionsError;
    }

    // Create a map of user_id -> expiry info for quick lookups
    const userExpiryMap = new Map<
      number,
      { status: string; end: string | null }
    >();

    if (subscriptionsData) {
      subscriptionsData.forEach((subscription: SubscriptionExpiryData) => {
        // Only store active subscriptions or the most recent one if we have multiple
        if (
          subscription.status === "active" ||
          !userExpiryMap.has(subscription.user_id)
        ) {
          userExpiryMap.set(subscription.user_id, {
            status: subscription.status,
            end: subscription.subscription_end,
          });
        }
      });
    }

    const now = new Date();
    const oneMonthFromNow = new Date();
    oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

    // Apply filtering based on expiry status
    switch (expiryFilter) {
      case "expired":
        // Users with expired subscriptions
        return users.filter((user) => {
          const expiryInfo = userExpiryMap.get(user.id);
          if (!expiryInfo || !expiryInfo.end) return false;
          return new Date(expiryInfo.end) < now;
        });

      case "expiring-soon":
        // Users with subscriptions expiring within a month
        return users.filter((user) => {
          const expiryInfo = userExpiryMap.get(user.id);
          if (!expiryInfo || !expiryInfo.end) return false;
          const expiryDate = new Date(expiryInfo.end);
          return expiryDate >= now && expiryDate <= oneMonthFromNow;
        });

      case "active":
        // Users with non-expired subscriptions (includes expiring soon)
        return users.filter((user) => {
          const expiryInfo = userExpiryMap.get(user.id);
          if (!expiryInfo || !expiryInfo.end) return false;
          const expiryDate = new Date(expiryInfo.end);
          return expiryDate >= now; // include both active and expiring-soon
        });

      case "none":
        // Users without any subscription
        return users.filter((user) => !userExpiryMap.has(user.id));

      default:
        return users;
    }
  } catch (error) {
    console.error("Error filtering users by expiry status:", error);
    throw error;
  }
};
